import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";
import AnimatedCounter from "./AnimatedCounter";

const IMAGES = {
  children:
    "https://images.unsplash.com/photo-1759143101324-d375443f1955?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNoaWxkcmVuJTIwbGVhcm5pbmclMjBzY2hvb2wlMjBydXJhbHxlbnwwfHx8fDE3NzgwMDUyNjF8MA&ixlib=rb-4.1.0&q=85",
  community:
    "https://images.unsplash.com/photo-1776570380445-1a419559f603?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBzb2xpZGFyaXR5JTIwaG9sZGluZyUyMGhhbmRzfGVufDB8fHx8MTc3ODAwNTI2MXww&ixlib=rb-4.1.0&q=85",
};

const Impact = () => {
  const { t, lang } = useLanguage();
  const [remoteItems, setRemoteItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/testimonials")
      .then(({ data }) => {
        if (!cancelled) setRemoteItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Map remote testimonials to local shape; fallback to static i18n items
  const items =
    remoteItems.length >= 3
      ? remoteItems.slice(0, 3).map((r) => ({
          quote: lang === "en" ? r.quote_en || r.quote_fr : r.quote_fr || r.quote_en,
          name: r.name,
          location: r.location,
        }))
      : t.impact.testimonials;

  return (
    <section
      id="impact"
      className="py-24 md:py-32 bg-[#F3F0EA]"
      data-testid="impact-section"
    >
      <div className="container-narrow">
        <div className="max-w-2xl">
          <span className="eyebrow" data-testid="impact-eyebrow">{t.impact.eyebrow}</span>
          <span className="gold-line mt-4 mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#0A192F] leading-[1.1] font-medium" data-testid="impact-title">
            {t.impact.title}
          </h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mt-14 bg-[#E5E7EB] rounded-2xl overflow-hidden">
          {t.impact.stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white p-8 md:p-10"
              data-testid={`impact-stat-${i}`}
            >
              <div className="font-serif text-4xl md:text-5xl text-[#002B49] font-semibold tracking-tight">
                <AnimatedCounter value={s.value} />
              </div>
              <div className="text-sm text-[#64748B] mt-2 uppercase tracking-wider">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bento grid: testimonials + images */}
        <div className="mt-16">
          <h3 className="font-serif text-2xl md:text-3xl text-[#0A192F] mb-8 font-medium" data-testid="testimonials-title">
            {t.impact.testimonialsTitle}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">
            {/* Image card 1 — tall */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:col-span-2 md:row-span-2 rounded-2xl overflow-hidden aspect-square md:aspect-auto"
            >
              <img
                src={IMAGES.children}
                alt="Enfants à l'école"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </motion.div>

            {/* Testimonial 1 */}
            {items[0] && (
              <Testimonial item={items[0]} delay={0.1} testid="testimonial-0" colSpan="md:col-span-4" />
            )}

            {/* Testimonial 2 */}
            {items[1] && (
              <Testimonial item={items[1]} delay={0.2} testid="testimonial-1" colSpan="md:col-span-2" />
            )}

            {/* Image card 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="md:col-span-2 rounded-2xl overflow-hidden aspect-video md:aspect-auto"
            >
              <img
                src={IMAGES.community}
                alt="Communauté"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </motion.div>

            {/* Testimonial 3 */}
            {items[2] && (
              <Testimonial item={items[2]} delay={0.4} testid="testimonial-2" colSpan="md:col-span-6" />
            )}
          </div>

          <div className="text-center mt-10">
            <Link
              to={lang === "en" ? "/testimonials" : "/temoignages"}
              className="inline-flex items-center gap-2 text-[#002B49] hover:text-[#C5A059] font-medium border-b border-[#002B49]/30 hover:border-[#C5A059] pb-1 transition-colors"
              data-testid="see-all-testimonials"
            >
              {lang === "fr" ? "Voir tous les témoignages" : "See all testimonials"}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const Testimonial = ({ item, delay, testid, colSpan }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className={`${colSpan} bg-white rounded-2xl p-8 md:p-10 shadow-[0_4px_30px_-10px_rgba(0,43,73,0.1)] border border-black/5 flex flex-col`}
    data-testid={testid}
  >
    <Quote size={28} className="text-[#C5A059] mb-4" />
    <p className="font-serif text-lg md:text-xl text-[#0A192F] leading-relaxed flex-1 italic">
      « {item.quote} »
    </p>
    <div className="mt-6 pt-6 border-t border-black/10">
      <div className="font-semibold text-[#002B49]">{item.name}</div>
      <div className="text-sm text-[#64748B] mt-0.5">{item.location}</div>
    </div>
  </motion.div>
);

export default Impact;
